import React, { useState } from "react"
import { useContent } from '../../contexts/ContentContext'
import SectionWrapper from "../ui/SectionWrapper"
import SectionLabel from "../ui/SectionLabel"
import CtaButton from "../ui/CtaButton"
import { X, Send } from "lucide-react"

/**
 * PodcastVideoSection — Video section with transcript panel
 *
 * MIGRATED to design tokens (Single Source of Truth):
 * - Font-family: font-secondary (headlines), font-primary (body)
 * - Label: typography-label (includes font-primary, text-label, label, text-color-secondary, letter-spacing)
 * - H2: text-h2 (36px) + color-heading
 * - Subline: text-body-lg (20px) + color-body
 * - Panel H3: text-h3 (30px) + color-heading
 * - Body: text-body (18px) + color-body
 * - Backgrounds: color-bg-light, color-card-overlay
 * - Borders: color-border
 *
 * FEATURES:
 * - YouTube video embed (iframe)
 * - Slide-in transcript panel with smooth animation
 * - Email form or Fluent Forms iframe
 * - Transitions via Tailwind (no inline styles)
 */
export default function PodcastVideoSection() {
  const { podcasts, podcastVideo } = useContent()
  const [panelOpen, setPanelOpen] = useState(false)
  // podcasts is an array on regression; other sites may omit / stub it
  const podcast = Array.isArray(podcasts) && podcasts.length > 0 ? podcasts[0] : null
  const fluentTranscriptUrl = import.meta.env.VITE_FLUENT_FORMS_TRANSCRIPT_URL

  // Must bail before any podcast.* access (site-switch safety)
  if (!podcast?.label) return null

  // Extract YouTube ID from URL
  const getYouTubeId = (url) => {
    const m = url.match(/(?:embed\/|watch\?v=|youtu\.be\/)([^?&]+)/)
    return m ? m[1] : url
  }
  const ytId = podcast.youtubeEmbedUrl ? getYouTubeId(podcast.youtubeEmbedUrl) : null

  // YouTube embed URL
  const youtubeEmbedUrl = ytId
    ? `https://www.youtube.com/embed/${ytId}?enablejsapi=1&origin=${window.location.origin}`
    : null

  return (
    <>
      <SectionWrapper id="podcast-video" data-testid="podcast-video-section">
          {/* Centered heading */}
          <div className="text-center max-w-centered-header mx-auto">
            <SectionLabel text={podcast.label} />

            <h2 className="typography-h2-mobile md:typography-h2 leading-tight heading-body-spacing">
              {podcast.headline}
            </h2>

            <p className="typography-body-lg leading-relaxed">
              {podcast.subline}
            </p>
          </div>

          {/* YouTube Video — centered iframe */}
          <div className="max-w-4xl mx-auto w-full" data-testid="podcast-video-embed">
            {youtubeEmbedUrl && (
              <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  src={youtubeEmbedUrl}
                  className="w-full h-full"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>

          {/* Transcript download button — centered (hidden until transcript available) */}
          <div className="mt-8 flex justify-center hidden">
            <CtaButton
              label={podcastVideo.downloadButtonLabel}
              variant="secondary"
              onClick={() => setPanelOpen(true)}
              className="animate-pulse-soft"
              data-testid="transcript-download-btn"
            />
          </div>
      </SectionWrapper>

      {/* ── Transcript Slide-in Panel ── */}
      {panelOpen && (
        <div
          className="fixed inset-0 bg-color-overlay-dark z-40 backdrop-blur-sm"
          onClick={() => setPanelOpen(false)}
          data-testid="transcript-panel-backdrop"
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-mobile-content sm:max-w-sm md:max-w-md lg:max-w-[420px] bg-color-bg-light shadow-2xl z-50 flex flex-col transition-slower ${
          panelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="transcript-panel"
      >
        <div className="flex items-start justify-between px-8 pt-8 pb-6 border-b border-color-light">
          <div className="min-w-0 flex-1">
            <p className="typography-label !mb-1">
              {podcastVideo.panelLabel}
            </p>

            <h3 className="typography-h3-mobile md:typography-h3 leading-tight">
              {podcastVideo.panelTitleLine1}<br />{podcastVideo.panelTitleLine2}
            </h3>
          </div>

          <button
            onClick={() => setPanelOpen(false)}
            className="touch-target flex items-center justify-center p-1.5 hover:opacity-50 transition-opacity flex-shrink-0"
            aria-label={podcastVideo.closeButtonLabel}
            data-testid="transcript-panel-close"
          >
            <X className="w-icon-md h-icon-md text-color-primary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          <p className="typography-body-mobile md:typography-body leading-relaxed content-spacing-lg">
            {podcastVideo.emailPrompt}
          </p>

          {fluentTranscriptUrl ? (
            <iframe
              src={fluentTranscriptUrl}
              className="w-full min-h-[280px] sm:min-h-[300px] md:min-h-[320px] border-0"
              title={podcastVideo.submitButtonLabel}
              data-testid="transcript-fluent-iframe"
            />
          ) : (
            <div className="space-y-4" data-testid="transcript-placeholder-form">
              <div>
                <label className="typography-label block mb-3">
                  {podcastVideo.nameLabel}
                </label>

                <input
                  type="text"
                  placeholder={podcastVideo.namePlaceholder}
                  className="w-full bg-color-card-overlay border border-color-light rounded-lg px-4 py-3 font-primary text-body text-color-text placeholder:text-color-secondary/50 focus:outline-none focus:border-color-primary/40 transition-colors"
                  data-testid="transcript-name-input"
                />
              </div>

              <div>
                <label className="typography-label block mb-3">
                  {podcastVideo.emailLabel}
                </label>

                <input
                  type="email"
                  placeholder={podcastVideo.emailPlaceholder}
                  className="w-full bg-color-card-overlay border border-color-light rounded-lg px-4 py-3 font-primary text-body text-color-text placeholder:text-color-secondary/50 focus:outline-none focus:border-color-primary/40 transition-colors"
                  data-testid="transcript-email-input"
                />
              </div>

              <button
                className="w-full flex items-center justify-center gap-2.5 bg-color-primary font-primary text-label label text-on-dark py-4 rounded-full hover:bg-color-secondary transition-colors transition-normal element-spacing-sm"
                data-testid="transcript-submit-btn"
              >
                <Send className="w-icon-sm h-icon-sm" />
                {podcastVideo.submitButtonLabel}
              </button>

              <p className="font-primary text-hint text-color-secondary/60 text-center pt-2">
                {podcastVideo.fluentFormsNote}
              </p>
            </div>
          )}
        </div>

        <div className="px-8 py-5 border-t border-color-light">
          <p className="font-primary text-hint text-color-secondary/50 text-center leading-relaxed">
            {podcastVideo.privacyNoteLine1}<br />{podcastVideo.privacyNoteLine2}
          </p>
        </div>
      </div>
    </>
  )
}
